/**
 * 世界级现代化项目卡片组件
 * 采用最新的设计趋势：Glassmorphism + Neumorphism + 微交互
 */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Card,
  Progress,
  Tag,
  Avatar,
  Space,
  Button,
  Tooltip,
  Badge,
} from 'antd';
import {
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  DollarOutlined,
  CalendarOutlined,
  TrophyOutlined,
  TeamOutlined,
  MoreOutlined,
  StarOutlined,
  FireOutlined,
  ThunderboltOutlined,
  RocketOutlined,
} from '@ant-design/icons';
import { emeraldColors } from '../../theme/emeraldTheme';
import './ModernProjectCard.css';

interface ModernProjectCardProps {
  project: any;
  onView?: (project: any) => void;
  onEdit?: (project: any) => void;
  onDelete?: (project: any) => void;
}

const ModernProjectCard: React.FC<ModernProjectCardProps> = ({
  project,
  onView,
  onEdit,
  onDelete,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isPressed, setIsPressed] = useState(false);
  const { t } = useTranslation();
  // 状态颜色映射
  const getStatusColor = (status: string) => {
    const colorMap: Record<string, string> = {
      active: emeraldColors.primary[500],
      completed: emeraldColors.functional.success,
      suspended: emeraldColors.functional.warning,
      cancelled: emeraldColors.functional.error,
    };
    return colorMap[status] || emeraldColors.neutral.gray400;
  };

  // 风险等级颜色
  const getRiskColor = (riskLevel: string) => {
    const colorMap: Record<string, string> = {
      low: emeraldColors.functional.success,
      medium: emeraldColors.functional.warning,
      high: emeraldColors.functional.error,
    };
    return colorMap[riskLevel] || emeraldColors.neutral.gray400;
  };

  // 阶段显示名称
  const getStageDisplayName = (stage: string) => {
    const stageMap: Record<string, string> = {
      detailed_assessment: t('project:common.stages.detailedAssessment'),
      due_diligence: t('project:common.stages.dueDiligence'),
      documentation: t('project:common.stages.documentation'),
      approval: t('project:common.stages.approval'),
      issuance: t('project:common.stages.issuance'),
      post_issuance: t('project:common.stages.postIssuance'),
    };
    return stageMap[stage] || stage;
  };

  // 获取项目优先级图标
  const getPriorityIcon = (aiScore: number) => {
    if (aiScore >= 90) return <RocketOutlined style={{ color: '#ff4d4f' }} />;
    if (aiScore >= 80) return <FireOutlined style={{ color: '#fa8c16' }} />;
    if (aiScore >= 70)
      return <ThunderboltOutlined style={{ color: '#faad14' }} />;
    return <StarOutlined style={{ color: '#52c41a' }} />;
  };

  // 获取进度条颜色
  const getProgressColor = (progress: number) => {
    if (progress >= 80) return ['#52c41a', '#73d13d'];
    if (progress >= 60) return ['#1890ff', '#40a9ff'];
    if (progress >= 40) return ['#faad14', '#ffc53d'];
    return ['#ff7875', '#ff9c6e'];
  };

  const cardActions = [
    <Tooltip title={t('project:common.viewDetails')} key="view">
      <Button
        type="text"
        icon={<EyeOutlined />}
        onClick={() => onView?.(project)}
        style={{ color: emeraldColors.primary[600] }}
      />
    </Tooltip>,
    <Tooltip title={t('project:common.editProject')} key="edit">
      <Button
        type="text"
        icon={<EditOutlined />}
        onClick={() => onEdit?.(project)}
        style={{ color: emeraldColors.neutral.gray600 }}
      />
    </Tooltip>,
    <Tooltip title={t('project:common.deleteProject')} key="delete">
      <Button
        type="text"
        icon={<DeleteOutlined />}
        onClick={() => onDelete?.(project)}
        style={{ color: emeraldColors.functional.error }}
      />
    </Tooltip>,
  ];

  // 处理卡片点击 - 整个卡片可点击进入详情
  const handleCardClick = (e: React.MouseEvent) => {
    // 如果点击的是操作按钮区域，不触发卡片点击
    const target = e.target as HTMLElement;
    if (target.closest('.ant-card-actions')) {
      return;
    }
    onView?.(project);
  };

  return (
    <Card
      className="modern-project-card"
      hoverable
      actions={cardActions}
      onClick={handleCardClick}
      style={{
        borderRadius: '16px',
        background: 'white',
        border: '1px solid #f0f0f0',
        boxShadow: isHovered
          ? '0 8px 24px rgba(0, 0, 0, 0.12)'
          : '0 2px 8px rgba(0, 0, 0, 0.06)',
        transition: 'all 0.3s ease',
        transform: isHovered ? 'translateY(-4px)' : 'translateY(0)',
        cursor: 'pointer',
      }}
      bodyStyle={{
        padding: '24px',
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* 项目头部 - 简洁设计 */}
      <div style={{ marginBottom: '20px' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '12px',
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <h3
              style={{
                margin: 0,
                fontSize: '18px',
                fontWeight: '600',
                color: '#262626',
                lineHeight: '1.4',
                marginBottom: '4px',
              }}
            >
              {project.project_name}
            </h3>
            <p
              style={{
                margin: 0,
                color: '#8c8c8c',
                fontSize: '14px',
                lineHeight: '1.5',
              }}
            >
              {project.issuer_name}
            </p>
          </div>
          <Avatar
            size={48}
            style={{
              backgroundColor: getStatusColor(project.overall_status),
              color: 'white',
              fontSize: '18px',
              fontWeight: '600',
              marginLeft: '16px',
            }}
          >
            {project.project_name?.substring(0, 2) || 'P'}
          </Avatar>
        </div>

        {/* 状态标签 - 简洁设计 */}
        <Space size={8} wrap>
          <Tag
            color={getStatusColor(project.overall_status)}
            style={{
              borderRadius: '4px',
              fontSize: '12px',
              border: 'none',
            }}
          >
            {project.status_display || t('project:common.projectStatus.active')}
          </Tag>
          <Tag
            color={emeraldColors.primary[500]}
            style={{
              borderRadius: '4px',
              fontSize: '12px',
              border: 'none',
            }}
          >
            {getStageDisplayName(project.current_stage)}
          </Tag>
          <Tag
            color={getRiskColor(project.risk_level)}
            style={{
              borderRadius: '4px',
              fontSize: '12px',
              border: 'none',
            }}
          >
            {project.risk_level === 'low'
              ? t('project:common.risk.low')
              : project.risk_level === 'medium'
                ? t('project:common.risk.medium')
                : t('project:common.risk.high')}
          </Tag>
        </Space>
      </div>

      {/* 项目指标 - 简洁设计 */}
      <div style={{ marginBottom: '20px' }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '16px',
            marginBottom: '16px',
          }}
        >
          <div
            style={{
              padding: '16px',
              background: '#fafafa',
              borderRadius: '8px',
              border: '1px solid #f0f0f0',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: '8px',
              }}
            >
              <DollarOutlined
                style={{
                  color: emeraldColors.primary[600],
                  marginRight: '8px',
                  fontSize: '16px',
                }}
              />
              <span
                style={{
                  fontSize: '12px',
                  color: '#8c8c8c',
                  fontWeight: '500',
                }}
              >
                {t('project:projectCard.issuanceScale')}
              </span>
            </div>
            <div
              style={{
                fontSize: '16px',
                fontWeight: '600',
                color: '#262626',
              }}
            >
              {project.bond_amount_display || t('project:common.tbd')}
            </div>
          </div>

          <div
            style={{
              padding: '16px',
              background: '#fafafa',
              borderRadius: '8px',
              border: '1px solid #f0f0f0',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: '8px',
              }}
            >
              <TrophyOutlined
                style={{
                  color: '#1890ff',
                  marginRight: '8px',
                  fontSize: '16px',
                }}
              />
              <span
                style={{
                  fontSize: '12px',
                  color: '#8c8c8c',
                  fontWeight: '500',
                }}
              >
                {t('project:projectCard.aiScore')}
              </span>
            </div>
            <div
              style={{
                fontSize: '16px',
                fontWeight: '600',
                color: '#262626',
              }}
            >
              {project.ai_score || 0}
              {t('project:common.points')}
            </div>
          </div>
        </div>

        {/* 进度条 - 简洁设计 */}
        <div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '8px',
            }}
          >
            <span
              style={{
                fontSize: '14px',
                fontWeight: '500',
                color: '#8c8c8c',
              }}
            >
              {t('project:projectCard.projectProgress')}
            </span>
            <span
              style={{
                fontSize: '14px',
                fontWeight: '600',
                color: emeraldColors.primary[600],
              }}
            >
              {project.progress_percentage || 0}%
            </span>
          </div>
          <Progress
            percent={project.progress_percentage || 0}
            strokeColor={emeraldColors.primary[500]}
            trailColor="#f5f5f5"
            strokeWidth={6}
            showInfo={false}
          />
        </div>
      </div>

      {/* 项目信息 - 简洁设计 */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          fontSize: '13px',
          color: '#8c8c8c',
          paddingTop: '16px',
          borderTop: '1px solid #f0f0f0',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <CalendarOutlined style={{ marginRight: '6px' }} />
          <span>
            {project.created_at_display || new Date().toLocaleDateString()}
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <TeamOutlined style={{ marginRight: '6px' }} />
          <span>
            {project.issuer_contact_name || t('project:projectCard.contact')}
          </span>
        </div>
      </div>
    </Card>
  );
};

export default ModernProjectCard;
